import { router } from "@inertiajs/react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { showDeleteModal } from "../../../helpers/modal";
import { useYearContext } from "../../../hooks/useYearContext";
import type { Coach } from "../../../models/coach";
import type { Pagination } from "../../../models/pagination";
import { admin_path, admins_path, edit_admin_path, new_admin_path } from "../../../routes";
import Users, { resetUserFilter, type UserFilterFormType } from "../Users";

type AdminsProps = {
  admins: Coach[];
  admin_names: string[];
  filter: {
    sort_key?: "email";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

export default function Admins({ admins, filter, admin_names, pagination }: AdminsProps) {
  const { pathWithYearContext } = useYearContext();
  const { sort_key, sort_direction, ...filterFormData } = filter;
  const [isFetching, setIsFetching] = useState(false);

  const sortStatus: DataTableSortStatus<Coach> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const handlePageUpdate = (filters: UserFilterFormType, sortStatus: DataTableSortStatus<Coach>, page: number) => {
    setIsFetching(true);
    router.get(
      pathWithYearContext(admins_path),
      {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      {
        onFinish: () => setIsFetching(false),
      },
    );
  };

  const onSortChange = (sortStatus: DataTableSortStatus<Coach>) => handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const onFilterSubmit = (values: UserFilterFormType) => handlePageUpdate(values, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const onDelete = (admin: Coach) =>
    showDeleteModal({
      onConfirm: () => router.delete(pathWithYearContext(admin_path, admin.id)),
      title: "Admin",
      article: "den",
    });
  const onEdit = (admin: Coach) => router.get(pathWithYearContext(edit_admin_path, admin.id));
  const onNew = () => router.get(pathWithYearContext(new_admin_path));

  return (
    <Users<Coach, UserFilterFormType>
      title="Admins"
      records={admins}
      onNew={onNew}
      onDelete={onDelete}
      onEdit={onEdit}
      sortStatus={sortStatus}
      onSortStatusChange={onSortChange}
      userTypeString="Admin"
      userTypeStringPlural="Admins"
      onReset={() => onFilterSubmit(resetUserFilter)}
      onFilterSubmit={onFilterSubmit}
      filterFormData={filterFormData}
      userNames={admin_names}
      pagination={pagination}
      onPageChange={onPageChange}
      fetching={isFetching}
    />
  );
}
